import type { FieldOptions } from '../../types/layout-items.tsx';
import { doesFieldTypeSupportOptions } from '../../utils/layout-items/does-field-type-support-options.tsx';

const mapOptimisticId = (o: unknown) => {
	if (
		o != null &&
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		typeof o.value === 'string' &&
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		typeof o.id === 'number' &&
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		!Number.isNaN(o.id)
	) {
		// optimisticId is used for rendering options, DnD, etc... We map the option id to ensure this method remains idempotent.
		return {
			...o,
			// @ts-expect-error - TS2571 - Object is of type 'unknown'.
			optimisticId: o.id.toString(),
		};
	}

	return o;
};

const mapDefaultValueFromOptions = (defaultValue: unknown, options: FieldOptions): unknown => {
	let matchingOption;
	// We need to copy the externalUuid from the options to the default value as existing default values currently do not have uuids
	// This will be able to be removed once there are external uuids on all existing default values
	if (
		defaultValue != null &&
		typeof defaultValue === 'object' &&
		// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'object'.
		typeof defaultValue.id === 'number'
	) {
		// @ts-expect-error - TS2339 - Property 'id' does not exist on type 'object'.
		matchingOption = options.find((option) => option.id === defaultValue.id);
	}
	return {
		// @ts-expect-error - Spread types may only be created from object types.
		...mapOptimisticId(defaultValue),
		externalUuid: matchingOption?.externalUuid,
		parentId: matchingOption?.parentId,
		parentExternalUuid: matchingOption?.parentExternalUuid,
	};
};

export const normalizeDefaultValue = (
	fieldType: string,
	defaultValue: unknown,
	options?: FieldOptions,
): unknown => {
	// GraphQL API (GET) will return empty default values as null, while the REST API (PUT) will return undefined.
	// We return undefined values as null to ensure we can reliably compare empty default values.
	if (defaultValue === undefined || defaultValue === null) {
		return undefined;
	}

	if (!doesFieldTypeSupportOptions(fieldType)) {
		return defaultValue;
	}

	// Ensure default values for field types which support options correctly map optimisticId. When an option is
	// selected as the default value on the UI it will have an optimisticId, so we must ensure default values from the
	// server also have this property or else dirty field checks will fail.
	if (options === undefined) {
		if (Array.isArray(defaultValue)) {
			return defaultValue.map((value) => mapOptimisticId(value));
		}
		return mapOptimisticId(defaultValue);
	}
	if (Array.isArray(defaultValue)) {
		return defaultValue.map((value) => mapDefaultValueFromOptions(value, options));
	}
	return mapDefaultValueFromOptions(defaultValue, options);
};
